<template>
  <div
    class="attachment-manager"
    :class="{ 'attachment-manager--has-controls': hasControls }"
  >
    <table
      v-if="internalValue && internalValue.length"
      class="uk-table uk-table-divider uk-margin-remove uk-table-middle"
    >
      <thead>
        <tr>
          <th v-if="hasControls" class="uk-table-shrink"></th>
          <th>{{$t('fields.thumb')}}</th>
          <th>{{$t('fields.fileName')}}</th>
          <th>{{$t('fields.remoteAttachment')}}</th>
          <th v-if="hasControls">{{$t('fields.heroImage')}}</th>
          <th v-if="hasControls">Sichtbar</th>
          <th v-if="hasControls"></th>
        </tr>
      </thead>
      <draggable
        v-model="internalValue"
        element="tbody"
        :options="{ handle: '.drag-handle'}"
        @end="onInput"
      >
        <tr
          v-for="(item, index) in internalValue"
          :key="item.id"
          class="attachment-manager__row"
        >
          <td v-if="hasControls" class="uk-table-preserve-width">
            <drag-handle :disabled="!orderable" />
          </td>
          <td class="attachment-manager__thumbnail-cell">
            <thumbnail :attachment="item" />
          </td>
          <td>
            <attachment-name :attachment="item" />
          </td>
          <td>
            <icon :title="item.remote" :name="remoteIconName(item)"/>
          </td>
          <td>
            <Checkbox v-model="item.isHeroImage" @change="setHeroImageAttachment($event, item)" />
          </td>
          <td>
            <Checkbox v-model="item.isVisible"/>
          </td>
          <td v-if="hasControls">
            <data-grid-actions
              :edit-route="{ name: 'edit-attachment',
                             params: { id: item.id } }"
              @remove="removeAttachmentWhenConfirmed(index)"
            />
          </td>
        </tr>
      </draggable>
      <tfoot v-if="hasControls">
        <tr>
          <td colspan="4" class="uk-padding-remove-left uk-padding-remove-right">
            <file-upload-button
              :attachment-type="attachmentType"
              multiple
              :disabled="disabled"
              @upload="addUploadedAttachments"
            />
            <common-button
              icon="album"
              @click="$refs.picker.show()"
              :disabled="disabled"
            >
              {{$t('actions.select')}}
            </common-button>
          </td>
        </tr>
      </tfoot>
    </table>

    <div class="uk-placeholder uk-text-center uk-margin-remove" v-else>
      <div>
        <slot name="empty">
          {{$t('hints.empty')}}
        </slot>
      </div>
      <div v-if="hasControls" class="uk-margin-top">
        <file-upload-button
          :attachment-type="attachmentType"
          multiple
          @upload="addUploadedAttachments"
        />
        <common-button icon="album" @click="$refs.picker.show()">
          {{$t('actions.select')}}
        </common-button>
      </div>
    </div>

    <attachment-picker
      ref="picker"
      :attachment-type="attachmentType"
      :selected-attachment-ids="selectedAttachmentIds"
      @select="addAttachments"
    />
  </div>
</template>

<script>
import UIkit from 'uikit';
import Draggable from 'vuedraggable';
import AttachmentName from '@/components/AttachmentName';
import AttachmentPicker from '@/components/AttachmentPicker';
import CommonButton from '@/components/CommonButton';
import DataGridActions from '@/components/DataGridActions';
import DragHandle from '@/components/DragHandle';
import FileUploadButton from '@/components/FileUploadButton';
import Input from '@/mixins/Input';
import Thumbnail from '@/components/Thumbnail';
import Checkbox from './Checkbox';
import Icon from './Icon';

export default {
  name: 'attachment-manager',
  mixins: [Input],
  components: {
    AttachmentName,
    AttachmentPicker,
    CommonButton,
    DataGridActions,
    DragHandle,
    Draggable,
    FileUploadButton,
    Thumbnail,
    Checkbox,
    Icon,
  },
  data() {
    return {
      internalValue: (this.value || []).slice(0),
      attachmentWithHeroImage: undefined,
    };
  },
  props: {
    value: {
      type: Array,
      default: () => [],
    },
    attachmentType: String,
    hasControls: {
      type: Boolean,
      default: true,
    },
  },
  watch: {
    value(value) {
      this.internalValue = (value || []).slice(0);
    },
  },
  computed: {
    selectedAttachmentIds() {
      return this.internalValue.map(attachment => attachment.id).sort();
    },
    orderable() {
      return !this.disabled && this.internalValue.length > 1;
    },
  },
  methods: {
    onInput() {
      this.clearErrors();
      this.$emit('input', this.internalValue);
      this.$emit('change', this.internalValue);
    },
    removeAttachment(index) {
      this.internalValue.splice(index, 1);
      this.onInput();
    },
    removeAttachmentWhenConfirmed(index) {
      UIkit.modal
           .confirm(this.$t('confirmations.remove'))
           .then(() => { this.removeAttachment(index); })
           .catch(() => { });
    },
    addAttachments(attachments) {
      this.internalValue = this.internalValue.concat(attachments);
      this.onInput();
    },
    addUploadedAttachments({ attachments }) {
      this.addAttachments(attachments);
    },
    setHeroImageAttachment(selected, selectedAttachment) {
      const newAttachments = this.internalValue.map((attachment) => {
        if (attachment.id === selectedAttachment.id) {
          attachment.isHeroImage = selected;
        } else {
          attachment.isHeroImage = false;
        }
        return attachment;
      });
      this.$emit('input', newAttachments);
      this.$emit('change', newAttachments);
    },
    remoteIconName(attachment) {
      return attachment.remote ? 'check' : 'close';
    },
  },
  i18n: {
    messages: {
      de: {
        actions: {
          select: 'Auswählen',
        },
        confirmations: {
          remove: 'Datei wirklich entfernen?',
        },
        hints: {
          addFiles: 'Dateien hinzufügen',
          empty: 'Keine Dateien vorhanden.',
        },
        titles: {
          addFiles: 'Dateien hinzufügen',
        },
      },
    },
  },
};
</script>

<style lang="scss" scoped>
@import '../theme/variables.scss';

.attachment-manager--has-controls .attachment-manager__row {
  user-select: none;
  transition: {
    duration: 250ms;
    property: box-shadow;
    timing-function: ease-in-out;
  }

  &.sortable-ghost {
    box-shadow: 0 0 10px rgba(black, 0.15);
  }
}
.attachment-manager__thumbnail-cell {
  width: 100px;
}
</style>



// WEBPACK FOOTER //
// AttachmentManager.vue?51cba807